/**
 * Options resolution – single place for all options-related logic.
 * Used by:
 * - field-config/process-static-content.js (check type field configs)
 * - field-config/process-dynamic-content.js (dynamic options by form values)
 * - candidate_application/process-static-content.js (candidate fields – resolveOptionsFromSource only)
 *
 * Order: constants → low-level resolution → filters → config → static resolution → strip → form-based → dynamic resolution.
 *
 * After resolution:
 * - processField (process-static-content) sets field.options = resolved array and deletes field.exclude / field.include
 *   so downstream (toQuestion, processDynamicField) does not reprocess.
 * - Resolved option lists are plain arrays of { name, value }; source/exclude/include/conditions are not kept on the field.
 * - Option-level conditions are evaluated in resolveOptionsWithFormValues; the `conditions` key is stripped from
 *   each option before pushing (optionWithoutConditions) so the final list has no condition keys.
 */
import { countries } from '../constants/countries';
import adminDivisions from '../constants/country-admin-divisions';
import evaluateFieldConditions from './evaluate-field-conditions';
import { readValueByName } from './field-paths';

// ============================================================================
// Constants
// ============================================================================

const SOURCES = {
  adminDivisions,
  countries,
};

// Base type configurations for country/state/region types (used by field config, not candidate_application)
export const BASE_TYPE_CONFIG = {
  country: { source: 'countries', valueKey: 'value' },
  country_alpha2: { source: 'countries', valueKey: 'alpha2' },
  country_alpha3: { source: 'countries', valueKey: 'alpha3' },
  au_state: { source: 'adminDivisions.AU_STATES', valueKey: 'value' },
  us_state: { source: 'adminDivisions.US_STATES', valueKey: 'value' },
  nz_region: { source: 'adminDivisions.NZ_REGIONS', valueKey: 'value' },
};

// ============================================================================
// Low-level: resolve from path (used by field config and candidate_application)
// ============================================================================

/**
 * Resolve options from a source path (e.g. "countries", "adminDivisions.AU_STATES").
 * Used by field config and candidate_application/process-static-content.js.
 */
export const resolveOptionsFromSource = (
  path,
  { nameKey = 'name', valueKey = 'value', exclude = [] } = {},
) => {
  if (!path || typeof path !== 'string') return [];

  const [sourceName, ...nestedKeys] = path.split('.');
  const sourceObject = SOURCES?.[sourceName];
  if (!sourceObject) return [];

  let resolved = sourceObject;
  for (const key of nestedKeys) {
    if (resolved == null) return [];
    resolved = resolved[key];
  }

  resolved = resolved ?? SOURCES?.[path];
  if (!resolved || typeof resolved !== 'object') return [];

  const rawOptions = Array.isArray(resolved) ? resolved : Object.values(resolved);

  return rawOptions
    .filter(opt => !exclude.includes(opt[valueKey]))
    .map(opt => ({
      name: opt[nameKey] ?? opt[valueKey],
      value: opt[valueKey],
    }));
};

// ============================================================================
// Filter helpers
// ============================================================================

const toFilterArray = v => (v == null ? [] : Array.isArray(v) ? v : [v]);

export const applyIncludeExcludeFilter = (options, include = [], exclude = []) => {
  if (include.length === 0 && exclude.length === 0) return options;
  return options.filter(opt => {
    const optValue = opt?.value ?? opt;
    if (include.length > 0 && !include.includes(optValue)) return false;
    if (exclude.length > 0 && exclude.includes(optValue)) return false;
    return true;
  });
};

// ============================================================================
// Config and type checks (field config source types)
// ============================================================================

// Exclude/include are read only from the options object (never from field root).
export const getOptionsConfigForSourceType = (field, existingOptions = null) => {
  const opts = existingOptions ?? field?.options ?? {};
  const isSourceObject = opts && typeof opts === 'object' && !Array.isArray(opts) && opts.source;
  if (isSourceObject) {
    return {
      ...opts,
      exclude: toFilterArray(opts.exclude),
      include: toFilterArray(opts.include),
    };
  }
  // For country/admin-division types: options may be { exclude, include } without source.
  return {
    exclude: toFilterArray(opts.exclude),
    include: toFilterArray(opts.include),
  };
};

export const hasSourceType = type => {
  if (!type) return false;
  const baseType = type.startsWith('multi_') ? type.substring(6) : type;
  return baseType in BASE_TYPE_CONFIG;
};

// ============================================================================
// Static resolution (type + config → array)
// ============================================================================

/**
 * Resolve options for source-based types (countries, states).
 * If config has source, use it; otherwise use BASE_TYPE_CONFIG from type.
 * Returns array of { name, value }; apply exclude/include via applyIncludeExcludeFilter.
 */
export const resolveOptions = (type, existingOptions) => {
  if (!type) return existingOptions;

  if (existingOptions && typeof existingOptions === 'object' && existingOptions.source) {
    const exclude = toFilterArray(existingOptions.exclude);
    const include = toFilterArray(existingOptions.include);
    const valueKey = existingOptions.valueKey || 'value';
    const options = resolveOptionsFromSource(existingOptions.source, {
      valueKey,
      exclude: [],
    });
    return applyIncludeExcludeFilter(options, include, exclude);
  }

  const baseType = type.startsWith('multi_') ? type.substring(6) : type;
  const config = BASE_TYPE_CONFIG[baseType];
  if (!config || !config.source) return existingOptions;

  const exclude = toFilterArray(existingOptions?.exclude);
  const include = toFilterArray(existingOptions?.include);
  const options = resolveOptionsFromSource(config.source, {
    valueKey: config.valueKey,
    exclude: [],
  });
  return applyIncludeExcludeFilter(options, include, exclude);
};

export const wrapMultipleChoiceOptions = (options, camelField) => {
  if (options && !Array.isArray(options) && Array.isArray(options.options)) return options;
  return {
    multipleSelection: camelField.multipleSelection || false,
    otherOption: camelField.otherOption || false,
    options: Array.isArray(options) ? options : [],
  };
};

// ============================================================================
// Strip config after resolution (avoid reprocessing)
// ============================================================================

/**
 * Remove exclude/include from field after options are resolved.
 * Call after setting field.options = resolvedArray so downstream code doesn't re-apply filters.
 */
export const stripOptionsConfigFromField = field => {
  if (!field || typeof field !== 'object') return field;
  const next = { ...field };
  delete next.exclude;
  delete next.include;
  return next;
};

// ============================================================================
// Form-based options (dynamic resolution)
// ============================================================================

export const hasFormBasedOptions = options => {
  if (!options || typeof options !== 'object' || Array.isArray(options)) return false;
  if (options.source && typeof options.source === 'string' && !options.value) {
    return !options.source.includes('.');
  }
  if (options.options && Array.isArray(options.options)) {
    return options.options.some(
      item =>
        item &&
        typeof item === 'object' &&
        item.source &&
        !item.value &&
        typeof item.source === 'string' &&
        !item.source.includes('.'),
    );
  }
  return false;
};

export const getOptionCountBehavior = options => {
  if (!Array.isArray(options)) return 'show';
  if (options.length === 0) return 'hide';
  if (options.length === 1) return 'auto';
  return 'show';
};

const extractOptionsFromFormValue = value => {
  if (value && typeof value === 'object' && !Array.isArray(value) && Array.isArray(value.options)) {
    return value.options;
  }
  return value;
};

const convertFormValueToOptions = (value, fieldName, optionsLookup = {}) => {
  if (value === undefined || value === null || value === '') return [];
  if (typeof value === 'string') return [{ name: value, value }];
  if (Array.isArray(value)) {
    const fieldLookupOptions = optionsLookup[fieldName] || [];
    const resolvedOptions = [];
    for (const valItem of value) {
      if (typeof valItem === 'string') {
        if (!resolvedOptions.some(opt => opt.value === valItem)) {
          const lookupOption = fieldLookupOptions.find(opt => opt.value === valItem);
          resolvedOptions.push({
            name: lookupOption?.name || valItem,
            value: valItem,
          });
        }
      } else if (valItem && typeof valItem === 'object' && valItem.value) {
        if (!resolvedOptions.some(opt => opt.value === valItem.value)) {
          resolvedOptions.push({
            name: valItem.name || valItem.value,
            value: valItem.value,
          });
        }
      }
    }
    return resolvedOptions;
  }
  return [];
};

const resolveFormFieldSource = (fieldName, formValues, optionsLookup = {}) => {
  let value = formValues[fieldName];
  value = extractOptionsFromFormValue(value);
  return convertFormValueToOptions(value, fieldName, optionsLookup);
};

export const resolveFilterValues = (filter, formValues = {}, fieldContext = {}) => {
  if (Array.isArray(filter)) return filter;
  if (typeof filter !== 'string') return [];
  if (filter.includes('.')) {
    const parts = filter.split('.');
    const source = parts[0];
    const path = parts.slice(1).join('.');
    const sourceData = fieldContext?.sources?.[source];
    if (!sourceData) return [];
    const value = readValueByName(sourceData, path);
    if (value === undefined || value === null) return [];
    if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === 'object' && value[0].value) {
        return value.map(item => item.value);
      }
      return value;
    }
    return [value];
  }
  const value = formValues[filter];
  if (value !== undefined && value !== null) {
    if (Array.isArray(value)) {
      if (value.length > 0 && typeof value[0] === 'object' && value[0].value) {
        return value.map(item => item.value);
      }
      return value;
    }
    return [value];
  }
  return [];
};

/**
 * Resolve options that depend on form values (form field references or option-level conditions).
 * optionsConfig: { source, include, exclude } (form field) or { options: [...], dynamic_dependency_codes }
 * Returns array of { name, value } (option-level conditions stripped from each option).
 */
export const resolveOptionsWithFormValues = (
  optionsConfig,
  formValues = {},
  optionsLookup = {},
  fieldContext = {},
) => {
  if (!optionsConfig || typeof optionsConfig !== 'object' || Array.isArray(optionsConfig)) {
    return optionsConfig;
  }

  if (optionsConfig.source && typeof optionsConfig.source === 'string' && !optionsConfig.value) {
    const source = optionsConfig.source;
    if (!source.includes('.')) {
      const resolvedOptions = resolveFormFieldSource(source, formValues, optionsLookup);
      const include = resolveFilterValues(optionsConfig.include, formValues, fieldContext);
      const exclude = resolveFilterValues(optionsConfig.exclude, formValues, fieldContext);
      return applyIncludeExcludeFilter(resolvedOptions, include, exclude);
    }
    return optionsConfig;
  }

  if (optionsConfig.options && Array.isArray(optionsConfig.options)) {
    const allOptions = [];
    for (const item of optionsConfig.options) {
      if (item && typeof item === 'object' && item.source && !item.value) {
        const source = item.source;
        if (typeof source === 'string' && !source.includes('.')) {
          const itemOptions = resolveFormFieldSource(source, formValues, optionsLookup);
          const include = resolveFilterValues(item.include, formValues, fieldContext);
          const exclude = resolveFilterValues(item.exclude, formValues, fieldContext);
          allOptions.push(...applyIncludeExcludeFilter(itemOptions, include, exclude));
        }
        continue;
      }
      if (item && typeof item === 'object' && item.value && item.name) {
        if (item.conditions) {
          const shouldShow = evaluateFieldConditions(item.conditions, formValues);
          if (shouldShow) {
            const { conditions, ...optionWithoutConditions } = item;
            allOptions.push(optionWithoutConditions);
          }
        } else {
          allOptions.push(item);
        }
        continue;
      }
      allOptions.push(item);
    }
    return allOptions;
  }

  return optionsConfig;
};
